/**
 * /equipment/reports/variance — Two-variance report.
 *
 * Compares allocated charge-outs (from posted time entry batches) vs
 * actual costs (fuel fills + repairs from service entries) per equipment
 * item, broken out into ownership and operating components.
 *
 * Positive variance = over-recovered (charged more to jobs than incurred).
 * Negative variance = under-recovered (real costs exceeded charge-out).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/equipment/reports/variance")({
  component: VarianceReportPage,
});

interface VarianceRow {
  equipment_id: string;
  equipment_code: string;
  equipment_name: string;
  allocated_ownership: string;
  allocated_operating: string;
  allocated_total: string;
  actual_ownership: string;
  actual_operating: string;
  actual_total: string;
  variance_ownership: string;
  variance_operating: string;
  variance_total: string;
}

interface VarianceReport {
  from_date: string | null;
  to_date: string | null;
  rows: VarianceRow[];
  grand_allocated: string;
  grand_actual: string;
  grand_variance: string;
}

function fmtMoney(v: string | number | null | undefined, showSign = false) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  const s = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n as number);
  return showSign && n > 0 ? `+${s}` : s;
}

function varianceColor(v: string) {
  const n = Number(v);
  if (n > 0) return "success.main";
  if (n < 0) return "error.main";
  return "text.primary";
}

function VarianceReportPage() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const params = new URLSearchParams();
  if (fromDate) params.set("from", fromDate);
  if (toDate) params.set("to", toDate);
  const qs = params.toString();

  const reportQ = useQuery({
    queryKey: ["equipment-variance-report", fromDate, toDate],
    queryFn: () =>
      api<VarianceReport>(
        `/api/construction/equipment/reports/variance${qs ? `?${qs}` : ""}`,
      ),
    enabled: submitted,
  });

  const report = reportQ.data;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Button
          component={Link}
          to="/equipment/reports/cost-by-job"
          size="small"
          variant="outlined"
        >
          ← Cost by job
        </Button>
        <Typography variant="h5" fontWeight={600}>
          Equipment Two-Variance Report
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Compares <strong>allocated</strong> charge-outs (ownership + operating rates × hours from
        posted batches) against <strong>actual</strong> costs (fuel fills, repairs, and other
        service entries). Positive variance = over-recovered; negative = under-recovered.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <TextField
            label="From date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setSubmitted(false); }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setSubmitted(false); }}
          />
          <Button
            variant="contained"
            onClick={() => setSubmitted(true)}
            disabled={reportQ.isFetching}
          >
            {reportQ.isFetching ? "Loading…" : "Run report"}
          </Button>
          {submitted && (
            <Button
              variant="outlined"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setSubmitted(false);
              }}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {!submitted && (
        <Alert severity="info">
          Select a date range (optional) and click Run report.
          Leave dates blank to see all-time variance.
        </Alert>
      )}

      {submitted && reportQ.isLoading && (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      )}

      {submitted && reportQ.isError && (
        <Alert severity="error">{String(reportQ.error)}</Alert>
      )}

      {report && (
        <Stack spacing={3}>
          {report.rows.length === 0 ? (
            <Alert severity="info">
              No data for this date range. Post some time entries and log
              service entries to populate the variance report.
            </Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell><strong>Equipment</strong></TableCell>
                    <TableCell align="right">Alloc. Ownership</TableCell>
                    <TableCell align="right">Alloc. Operating</TableCell>
                    <TableCell align="right"><strong>Alloc. Total</strong></TableCell>
                    <TableCell align="right">Actual Ownership</TableCell>
                    <TableCell align="right">Actual Operating</TableCell>
                    <TableCell align="right"><strong>Actual Total</strong></TableCell>
                    <TableCell align="right">Var. Ownership</TableCell>
                    <TableCell align="right">Var. Operating</TableCell>
                    <TableCell align="right"><strong>Variance Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((row) => (
                    <TableRow key={row.equipment_id} hover>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            fontFamily="monospace"
                          >
                            {row.equipment_code}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {row.equipment_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {fmtMoney(row.allocated_ownership)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {fmtMoney(row.allocated_operating)}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{fmtMoney(row.allocated_total)}</strong>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {fmtMoney(row.actual_ownership)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12 }}>
                        {fmtMoney(row.actual_operating)}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{fmtMoney(row.actual_total)}</strong>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: 12, color: varianceColor(row.variance_ownership) }}
                      >
                        {fmtMoney(row.variance_ownership, true)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontSize: 12, color: varianceColor(row.variance_operating) }}
                      >
                        {fmtMoney(row.variance_operating, true)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: varianceColor(row.variance_total), fontWeight: 700 }}
                      >
                        {fmtMoney(row.variance_total, true)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Grand total row */}
                  <TableRow sx={{ bgcolor: "grey.100", fontWeight: 700 }}>
                    <TableCell><strong>Grand Total</strong></TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align="right">
                      <strong>{fmtMoney(report.grand_allocated)}</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align="right">
                      <strong>{fmtMoney(report.grand_actual)}</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell
                      align="right"
                      sx={{
                        color: varianceColor(report.grand_variance),
                        fontWeight: 700,
                        fontSize: 14,
                      }}
                    >
                      {fmtMoney(report.grand_variance, true)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}

          <Typography variant="caption" color="text.secondary">
            Allocated = ownership_amount + operating_amount summed from posted equipment
            cost batches (status = posted). Actual = total_cost summed from service
            entries (fuel fills, repairs, inspections, fixed costs) tagged to their
            respective cost component. Transport costs are excluded from this report.
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
